import { BarChart, ChartsTooltipContainer, ChartContainer, ChartsGrid, ChartsAxis, ChartsClipPath } from '@mui/x-charts';
import { useItemTooltip } from '@mui/x-charts/ChartsTooltip';
import { useXScale, useYScale, getValueToPositionMapper } from '@mui/x-charts/hooks';
import type { ScaleBand } from 'd3-scale';
import moment from 'moment-timezone';
import React, { useMemo, useId, useState, useRef, useEffect } from 'react';
import { PersonVitalTimeFrame, DetailedSleepStage } from '@redux/person/personTypes';
import { CircledCustomIcon, TableIcon } from 'components/Icons';
import { Text } from 'components/Typography';
import { ChartProps } from 'containers/PatientDashboard/components/Charts/Charts';
import { createAggregatedSleepTooltip } from 'utils/charts';
import { Color } from 'utils/color';
import { getDateFromStringIgnoreTimezone, getFormatedTime, formatMinutesToHoursAndMinutes } from 'utils/time';
import style from './SleepBarChart.scss';

type SleepBarChartProps = Omit<ChartProps, 'vitalType' | 'isLoadingData'>;

const CHART_HEIGHT = 466;
const TOOLTIP_WIDTH = 160;

// Merges overlapping or contiguous intervals
const mergeIntervals = (intervals: { startMin: number; endMin: number }[]) => {
  if (intervals.length === 0) return [];
  const sorted = [...intervals].sort((a, b) => a.startMin - b.startMin);
  const merged = [sorted[0]];
  for (let i = 1; i < sorted.length; i++) {
    const last = merged[merged.length - 1];
    const current = sorted[i];
    if (current.startMin <= last.endMin) {
      last.endMin = Math.max(last.endMin, current.endMin);
    } else {
      merged.push(current);
    }
  }
  return merged;
};

export const bucketSleepData = (
  events: { startTime: string; endTime: string; detailedSleepStage: DetailedSleepStage }[],
  timeFrame: PersonVitalTimeFrame,
  selectedDate: Date,
  timeZoneId: string
) => {
  let chartHasData = false;
  const daysCount = timeFrame === PersonVitalTimeFrame.SEVEN_DAYS ? 7 : 30;
  const endDay = moment.tz(selectedDate, timeZoneId).add(1, 'day').hour(17).minute(0).second(0).millisecond(0);
  const startDay = endDay.clone().subtract(daysCount, 'days');
  const daysArr = Array.from({ length: daysCount }, (_, i) => startDay.clone().add(i, 'days'));
  const categoriesArr = daysArr.map((d) => getFormatedTime(d.toDate().toString(), 'MMM. D'));
  const bucketStartsArr = daysArr.map((d) => d.clone());
  const bucketEvents: { startMin: number; endMin: number; stage: DetailedSleepStage }[][] = daysArr.map(() => []);

  events.forEach((entry) => {
    const s = moment.tz(entry.startTime, timeZoneId);
    const e = moment.tz(entry.endTime, timeZoneId);
    bucketStartsArr.forEach((bs, idx) => {
      const be = bs.clone().add(24, 'hours');
      if (e.isBefore(bs) || s.isSameOrAfter(be)) return;
      const segStart = moment.max(s, bs);
      const segEnd = moment.min(e, be);
      const startMin = Math.max(0, Math.min(1440, segStart.diff(bs, 'minutes')));
      const endMin = Math.max(0, Math.min(1440, segEnd.diff(bs, 'minutes')));
      if (startMin < endMin) {
        bucketEvents[idx].push({ startMin, endMin, stage: entry.detailedSleepStage });
      }
    });
  });

  const buckets = daysArr.map((_, idx) => {
    const dayEvents = bucketEvents[idx];
    if (dayEvents.length > 0) chartHasData = true;

    const eventsByStage: Record<DetailedSleepStage, { startMin: number; endMin: number }[]> = {
      [DetailedSleepStage.AWAKE]: [],
      [DetailedSleepStage.LIGHT_SLEEP]: [],
      [DetailedSleepStage.DEEP_SLEEP]: [],
      [DetailedSleepStage.REM_SLEEP]: []
    };

    dayEvents.forEach((evt) => {
      eventsByStage[evt.stage].push(evt);
    });

    const mergedEventsByStage = {
      awake: mergeIntervals(eventsByStage[DetailedSleepStage.AWAKE]),
      light: mergeIntervals(eventsByStage[DetailedSleepStage.LIGHT_SLEEP]),
      deep: mergeIntervals(eventsByStage[DetailedSleepStage.DEEP_SLEEP]),
      rem: mergeIntervals(eventsByStage[DetailedSleepStage.REM_SLEEP])
    };

    const totalDuration = (intervals: { startMin: number; endMin: number }[]) => intervals.reduce((sum, iv) => sum + (iv.endMin - iv.startMin), 0);

    const awake = totalDuration(mergedEventsByStage.awake);
    const light = totalDuration(mergedEventsByStage.light);
    const deep = totalDuration(mergedEventsByStage.deep);
    const rem = totalDuration(mergedEventsByStage.rem);

    const label = categoriesArr[idx];
    return {
      label,
      awake,
      light,
      deep,
      rem,
      tooltip: createAggregatedSleepTooltip(label, awake, light, deep, rem, timeFrame)
    };
  });

  return {
    hasData: chartHasData,
    days: daysArr,
    categories: categoriesArr,
    eventsByDay: bucketEvents,
    aggregatedBuckets: buckets,
    bucketStarts: bucketStartsArr
  } as const;
};

// Timeline chart for detailed sleep stages over rolling days (7 or 30)
// Displays 5pm–5pm buckets, so a bucket for May 5 will cover 5pm May 5 to 5pm May 6
const SleepStageTimelineChart: React.FC<SleepBarChartProps> = ({ timeZoneId, personVitalInsights, timeFrame, selectedDate, onDaySelect, onChartDataAvailabilityChange }) => {
  const scale = timeFrame === PersonVitalTimeFrame.SEVEN_DAYS ? 1 : 0.8;
  // Memoize chart data for 5pm–5pm buckets
  const { hasData, days, categories, eventsByDay, aggregatedBuckets, bucketStarts } = useMemo(
    () => bucketSleepData(personVitalInsights?.listOfAggregatedSleepData || [], timeFrame, selectedDate, timeZoneId),
    [personVitalInsights?.listOfAggregatedSleepData, timeFrame, selectedDate, timeZoneId]
  );

  // Notify parent about data availability
  React.useEffect(() => {
    onChartDataAvailabilityChange?.(hasData);
  }, [hasData, onChartDataAvailabilityChange]);
  // Vertical axis spans full 24h from bucket start; ticks every 2h
  const yMin = 0;
  const yMax = 24 * 60;
  const yTicks = Array.from({ length: yMax / 120 + 1 }, (_, i) => i * 120);
  // Base moment for axis labels: first bucket start
  const bucketStart = bucketStarts[0];
  const clipPathId = useId();
  const containerRef = useRef<HTMLDivElement>(null);
  const [tooltipState, setTooltipState] = useState<{ index: number; x: number; y: number } | null>(null);
  // ChartContainer margins used for positioning
  const margins = { top: 20, right: 20, bottom: 30, left: 30 };

  // Hide tooltip if mouse moves outside component bounds
  useEffect(() => {
    if (!tooltipState) return;
    const handleDocMouseMove = (e: MouseEvent) => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const { clientX, clientY } = e;
      if (clientX < rect.left || clientX > rect.right || clientY < rect.top || clientY > rect.bottom) {
        setTooltipState(null);
      }
    };
    document.addEventListener('mousemove', handleDocMouseMove);
    return () => {
      document.removeEventListener('mousemove', handleDocMouseMove);
    };
  }, [tooltipState]);

  // render rectangles using chart scales and show tooltips
  const Rectangles: React.FC = () => {
    const xScale = useXScale();
    const yScale = useYScale();
    const getX = getValueToPositionMapper(xScale);
    const getY = getValueToPositionMapper(yScale);
    const bandwidth = (xScale as ScaleBand<any>).bandwidth?.() ?? 0;
    return (
      <>
        {/* Transparent overlay rectangles for tooltip per day */}
        {aggregatedBuckets.map((bucket, i) => (
          <rect
            key={`tooltip-${i}`}
            x={getX(i) - bandwidth / 2}
            y={getY(yMin)}
            width={bandwidth}
            height={getY(yMax) - getY(yMin)}
            fill="transparent"
            pointerEvents="all"
            onClick={() => {
              const day = bucketStarts[i].clone().startOf('day').toDate();
              onDaySelect?.(day);
            }}
            onMouseMove={(e) => {
              if (!containerRef.current) return;
              const rectBounds = containerRef.current.getBoundingClientRect();
              const y = e.clientY - rectBounds.top;
              let x = getX(i) + margins.left;
              const containerWidth = rectBounds.width;
              const halfWidth = TOOLTIP_WIDTH / 2;
              // Clamp tooltip X within container bounds
              x = Math.max(halfWidth, Math.min(x, containerWidth - halfWidth));
              setTooltipState({ index: i, x, y });
            }}
            onMouseLeave={() => {
              setTooltipState(null);
            }}
          />
        ))}
        {/* Actual sleep stage segments */}
        {eventsByDay.map((dayEvents, i) =>
          dayEvents.map((evt, idx) => (
            <rect
              key={`rect-${i}-${idx}`}
              x={getX(i) - (bandwidth / 2) * scale}
              y={getY(evt.startMin)}
              width={bandwidth * scale}
              height={getY(evt.endMin) - getY(evt.startMin)}
              fill={
                evt.stage === DetailedSleepStage.AWAKE
                  ? Color.DARK_BLUE_100
                  : evt.stage === DetailedSleepStage.LIGHT_SLEEP
                    ? Color.DARK_BLUE_300
                    : evt.stage === DetailedSleepStage.DEEP_SLEEP
                      ? Color.DARK_BLUE_500
                      : Color.DARK_BLUE_700
              }
              style={{ pointerEvents: 'none' }}
            />
          ))
        )}
      </>
    );
  };
  const chartContent = useMemo(
    () => (
      <ChartContainer
        height={CHART_HEIGHT}
        margin={margins}
        series={[]}
        dataset={[]}
        xAxis={[
          {
            data: days.map((_, i) => i),
            scaleType: 'band',
            valueFormatter: (value: number) => categories[value] || '',
            tickLabelStyle: { fontFamily: 'Outfit', fill: Color.GRAY },
            disableLine: true
          }
        ]}
        yAxis={[
          {
            min: yMin,
            max: yMax,
            tickInterval: yTicks,
            // Format ticks from bucketStart (5pm) through next day
            valueFormatter: (v: number) => bucketStart.clone().tz(timeZoneId).add(v, 'minutes').format('h A'),
            tickLabelStyle: { fontFamily: 'Outfit', fill: Color.GRAY },
            disableLine: true,
            disableTicks: true,
            reverse: true
          }
        ]}
      >
        <ChartsGrid horizontal />
        <g clipPath={`url(#${clipPathId})`}>
          <Rectangles />
        </g>
        <ChartsAxis />
        <ChartsClipPath id={clipPathId} />
      </ChartContainer>
    ),
    [clipPathId, margins, days, categories, yMin, yMax, yTicks, aggregatedBuckets, eventsByDay, timeZoneId]
  );
  if (!hasData) {
    return (
      <div className={style.noDataChartContainer}>
        <CircledCustomIcon icon={TableIcon} />
        <Text>No data to display</Text>
      </div>
    );
  }
  return (
    <div className={style.chartContainer} ref={containerRef} style={{ position: 'relative' }} onMouseLeave={() => setTooltipState(null)}>
      {chartContent}
      {tooltipState && (
        <div
          style={{
            position: 'absolute',
            left: tooltipState.x,
            top: tooltipState.y,
            transform: 'translate(-50%, -100%)',
            pointerEvents: 'none',
            zIndex: 10
          }}
          dangerouslySetInnerHTML={{ __html: aggregatedBuckets[tooltipState.index].tooltip }}
        />
      )}
    </div>
  );
};

export const SleepBarChart: React.FC<SleepBarChartProps> = ({ timeZoneId, personVitalInsights, timeFrame, selectedDate, onDaySelect, onChartDataAvailabilityChange }) => {
  // render detailed timeline for 7 or 30-day timeframes
  if (timeFrame === PersonVitalTimeFrame.SEVEN_DAYS || timeFrame === PersonVitalTimeFrame.THIRTY_DAYS) {
    return (
      <SleepStageTimelineChart
        timeZoneId={timeZoneId}
        personVitalInsights={personVitalInsights}
        timeFrame={timeFrame}
        selectedDate={selectedDate}
        onDaySelect={onDaySelect}
        onChartDataAvailabilityChange={onChartDataAvailabilityChange}
      />
    );
  }

  // Otherwise render a stacked bar chart for the 12-month timeframe
  const dataset = useMemo(() => {
    if (!personVitalInsights?.listOfAggregatedTotalSleepData) {
      return [];
    }
    const numBuckets = 12;
    const units = timeFrame === PersonVitalTimeFrame.TWELVE_MONTHS ? 'months' : 'days';

    const endMoment = moment.tz(selectedDate, timeZoneId);
    const end = endMoment.isSame(moment(), 'day') ? endMoment : endMoment.endOf('day');

    let start = end.clone();
    if (units === 'months') {
      start.startOf('month');
      start.subtract(numBuckets - 1, units);
    } else {
      start.startOf('day');
      start.subtract(numBuckets - 1, units);
    }

    const buckets: { label: string; awake: number; light: number; deep: number; rem: number; tooltip: string }[] = [];
    for (let i = 0; i < numBuckets; i++) {
      const date = start.clone().add(i, units).toDate();
      const format = 'MMM YYYY';
      buckets.push({
        label: getFormatedTime(date.toString(), format),
        awake: 0,
        light: 0,
        deep: 0,
        rem: 0,
        tooltip: ''
      });
    }

    personVitalInsights.listOfAggregatedTotalSleepData.forEach((entry) => {
      const bucketIndex = moment(getDateFromStringIgnoreTimezone(entry.bucketLocal)).diff(start, units);
      if (bucketIndex >= 0 && bucketIndex < numBuckets) {
        const minutes = timeFrame === PersonVitalTimeFrame.TWELVE_MONTHS ? Math.round(entry.totalMinutes / (entry.distinctDays || 1)) : entry.totalMinutes;
        switch (entry.sleepStage) {
          case DetailedSleepStage.AWAKE:
            buckets[bucketIndex].awake += minutes;
            break;
          case DetailedSleepStage.LIGHT_SLEEP:
            buckets[bucketIndex].light += minutes;
            break;
          case DetailedSleepStage.DEEP_SLEEP:
            buckets[bucketIndex].deep += minutes;
            break;
          case DetailedSleepStage.REM_SLEEP:
            buckets[bucketIndex].rem += minutes;
            break;
        }
      }
    });
    buckets.forEach((bucket) => {
      bucket.tooltip = createAggregatedSleepTooltip(bucket.label, bucket.awake, bucket.light, bucket.deep, bucket.rem, timeFrame);
    });
    return buckets;
  }, [personVitalInsights, timeFrame, selectedDate, timeZoneId]);

  const hasData = dataset.some((d) => d.awake > 0 || d.light > 0 || d.deep > 0 || d.rem > 0);

  // Notify parent about data availability
  React.useEffect(() => {
    onChartDataAvailabilityChange?.(hasData);
  }, [hasData, onChartDataAvailabilityChange]);

  const series = useMemo(
    () => [
      // Order series so stacked bars appear from top (awake) to bottom (REM)
      {
        dataKey: 'rem',
        label: 'REM Sleep',
        stack: 'sleep',
        color: Color.DARK_BLUE_700,
        valueFormatter: (value) => formatMinutesToHoursAndMinutes(value ?? 0, true)
      },
      {
        dataKey: 'deep',
        label: 'Deep Sleep',
        stack: 'sleep',
        color: Color.DARK_BLUE_500,
        valueFormatter: (value) => formatMinutesToHoursAndMinutes(value ?? 0, true)
      },
      {
        dataKey: 'light',
        label: 'Light Sleep',
        stack: 'sleep',
        color: Color.DARK_BLUE_300,
        valueFormatter: (value) => formatMinutesToHoursAndMinutes(value ?? 0, true)
      },
      {
        dataKey: 'awake',
        label: 'Awake',
        stack: 'sleep',
        color: Color.DARK_BLUE_100,
        valueFormatter: (value) => formatMinutesToHoursAndMinutes(value ?? 0, true)
      }
    ],
    []
  );

  const SleepBarChartTooltip: React.FC = () => {
    const tooltipData = useItemTooltip();
    if (!tooltipData || tooltipData.identifier.dataIndex == null) {
      return null;
    }
    const idx = tooltipData.identifier.dataIndex;
    const dp = dataset[idx];
    return <div dangerouslySetInnerHTML={{ __html: dp.tooltip }} />;
  };

  return dataset.length > 0 && hasData ? (
    <div className={style.chartContainer}>
      <BarChart
        dataset={dataset}
        series={series}
        xAxis={[
          {
            dataKey: 'label',
            disableLine: true,
            disableTicks: true,
            tickLabelStyle: {
              fontFamily: 'Outfit',
              fill: Color.GRAY
            }
          }
        ]}
        yAxis={[
          {
            max: 1440,
            disableLine: true,
            disableTicks: true,
            valueFormatter: (value) => formatMinutesToHoursAndMinutes(value),
            tickInterval: [120, 240, 360, 480, 600, 720, 840, 960, 1080, 1200, 1320, 1440],
            tickLabelStyle: {
              fontFamily: 'Outfit',
              fill: Color.GRAY
            }
          }
        ]}
        grid={{ horizontal: true }}
        hideLegend
        height={CHART_HEIGHT}
        // Tooltip handled via custom container
        slotProps={{ tooltip: { trigger: 'none' } }}
      >
        <ChartsTooltipContainer trigger="item">
          <SleepBarChartTooltip />
        </ChartsTooltipContainer>
      </BarChart>
    </div>
  ) : (
    <div className={style.noDataChartContainer}>
      <CircledCustomIcon icon={TableIcon} />
      <Text>No data to display</Text>
    </div>
  );
};

export default SleepBarChart;
